import { Page } from "./common";
import { PageHead } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, useRateCard, useStore, money } from "../lib/store";
import { DEFAULT_BUILD_RATES, DEFAULT_FX, DEFAULT_RATE_CURRENCY, learnedRates } from "../lib/buildCost";
import { useIsAdmin } from "../lib/auth";

export default function Rates() {
  const rc = useRateCard();
  const admin = useIsAdmin();
  const phaseSum = Math.round(rc.phases.reduce((s, p) => s + p.pct, 0) * 100);

  return (
    <Page max={880}>
      <PageHead kicker="Studio · Settings" title="Rates & fees">
        {admin && <button className="btn ghost sm" onClick={() => actions.resetRateCard()}>Reset to defaults</button>}
      </PageHead>
      <p className="muted reveal" style={{ maxWidth: 620, marginBottom: 22 }}>
        {admin ? "Your studio's rate card — it drives every estimate and quotation. Fees are a % of construction budget with a floor." : "The studio rate card. Only the admin can change this."}
      </p>

      {/* disciplines */}
      <div className="card reveal" style={{ overflow: "hidden", marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 140px", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--line)" }}>
          <span className="kicker" style={{ fontSize: 9.5 }}>Discipline</span>
          <span className="kicker" style={{ fontSize: 9.5, textAlign: "right" }}>Fee %</span>
          <span className="kicker" style={{ fontSize: 9.5, textAlign: "right" }}>Floor fee</span>
        </div>
        {rc.disciplines.map((d) => (
          <div key={d.key} style={{ display: "grid", gridTemplateColumns: "1fr 120px 140px", gap: 12, padding: "12px 20px", borderTop: "1px solid var(--line)", alignItems: "center" }}>
            <div><div style={{ fontWeight: 500, fontSize: 14 }}>{d.label}</div><div className="dim" style={{ fontSize: 12 }}>{d.blurb}</div></div>
            <NumField suffix="%" disabled={!admin} value={+(d.pct * 100).toFixed(2)} onChange={(v) => actions.updateDiscipline(d.key, { pct: v / 100 })} />
            <NumField prefix="$" disabled={!admin} value={d.floor} step={500} onChange={(v) => actions.updateDiscipline(d.key, { floor: v })} />
          </div>
        ))}
      </div>

      {/* add-ons */}
      <h3 className="serif reveal" style={{ fontSize: 20, marginBottom: 12 }}>Optional services</h3>
      <div className="card reveal" style={{ overflow: "hidden", marginBottom: 20 }}>
        {rc.addons.map((a, i) => (
          <div key={a.key} style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 12, padding: "12px 20px", borderTop: i ? "1px solid var(--line)" : "none", alignItems: "center" }}>
            <div><div style={{ fontWeight: 500, fontSize: 14 }}>{a.label}</div><div className="dim" style={{ fontSize: 12 }}>{a.kind === "fixed" ? "fixed fee" : "% of budget"}</div></div>
            {a.kind === "fixed"
              ? <NumField prefix="$" disabled={!admin} value={a.value} step={250} onChange={(v) => actions.updateAddon(a.key, v)} />
              : <NumField suffix="%" disabled={!admin} value={+(a.value * 100).toFixed(2)} onChange={(v) => actions.updateAddon(a.key, v / 100)} />}
          </div>
        ))}
      </div>

      {/* additional (out-of-scope) services */}
      <div className="row between reveal" style={{ alignItems: "flex-end", marginBottom: 12 }}>
        <div>
          <h3 className="serif" style={{ fontSize: 20 }}>Additional services &amp; charges</h3>
          <p className="dim" style={{ fontSize: 12.5, marginTop: 2, maxWidth: 560 }}>Work that falls <b>outside the agreed scope</b> — billed per occurrence, as and when instructed. Issued as a rate schedule with every quotation and engagement; not part of the project fee.</p>
        </div>
        {admin && <button className="btn ghost sm" onClick={() => actions.addService()}><Icon.plus width={14} />Add service</button>}
      </div>
      <div className="card reveal" style={{ overflow: "hidden", marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 120px 96px 34px", gap: 12, padding: "12px 20px", borderBottom: "1px solid var(--line)" }}>
          <span className="kicker" style={{ fontSize: 9.5 }}>Service</span>
          <span className="kicker" style={{ fontSize: 9.5 }}>Unit</span>
          <span className="kicker" style={{ fontSize: 9.5, textAlign: "right" }}>Rate</span>
          <span className="kicker" style={{ fontSize: 9.5, textAlign: "right" }}>Incl. free</span>
          <span />
        </div>
        {(rc.services ?? []).length === 0 && <div className="dim" style={{ padding: "16px 20px", fontSize: 13 }}>No additional services defined.{admin ? " Add one above." : ""}</div>}
        {(rc.services ?? []).map((s) => (
          <div key={s.key} style={{ padding: "12px 20px", borderTop: "1px solid var(--line)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 120px 96px 34px", gap: 12, alignItems: "center" }}>
              {admin
                ? <input className="input" value={s.label} onChange={(e) => actions.updateService(s.key, { label: e.target.value })} style={{ padding: "7px 10px", fontSize: 13.5, fontWeight: 500 }} />
                : <div style={{ fontWeight: 500, fontSize: 14 }}>{s.label}</div>}
              {admin
                ? <div className="row" style={{ gap: 5 }}><span className="dim" style={{ fontSize: 12 }}>per</span><input className="input" value={s.unit} onChange={(e) => actions.updateService(s.key, { unit: e.target.value })} style={{ padding: "7px 9px", fontSize: 13 }} /></div>
                : <div className="dim mono" style={{ fontSize: 12.5 }}>per {s.unit}</div>}
              <NumField prefix="$" disabled={!admin} value={s.rate} step={25} onChange={(v) => actions.updateService(s.key, { rate: v })} />
              <NumField disabled={!admin} value={s.included ?? 0} step={1} onChange={(v) => actions.updateService(s.key, { included: v || undefined })} />
              {admin
                ? <button className="iconbtn" title="Remove" onClick={() => actions.removeService(s.key)} style={{ width: 30, height: 30, justifySelf: "end" }}><Icon.close width={13} /></button>
                : <span />}
            </div>
            {admin
              ? <input className="input" value={s.blurb} onChange={(e) => actions.updateService(s.key, { blurb: e.target.value })} placeholder="What this covers / when it applies…" style={{ padding: "6px 10px", fontSize: 12, marginTop: 8, color: "var(--ink-2)" }} />
              : s.blurb && <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>{s.blurb}{s.included ? ` · first ${s.included} included in the fee` : ""}</div>}
          </div>
        ))}
      </div>

      {/* complexity + phases */}
      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div className="card pad reveal">
          <div className="kicker" style={{ marginBottom: 12 }}>Complexity multipliers</div>
          <div className="col" style={{ gap: 10 }}>
            {rc.complexity.map((c) => (
              <div key={c.key} className="row between"><span style={{ fontSize: 14 }}>{c.label}</span><NumField suffix="×" disabled={!admin} step={0.01} value={+c.mult.toFixed(2)} onChange={(v) => actions.updateComplexity(c.key, v)} /></div>
            ))}
          </div>
        </div>
        <div className="card pad reveal">
          <div className="row between" style={{ marginBottom: 12 }}><span className="kicker">Disbursement phases</span><span className="mono" style={{ fontSize: 12, color: phaseSum === 100 ? "var(--ok)" : "var(--warn)" }}>{phaseSum}%</span></div>
          <div className="col" style={{ gap: 10 }}>
            {rc.phases.map((ph) => (
              <div key={ph.key} className="row between"><span style={{ fontSize: 13.5 }} className="row">{ph.label}{ph.construction && <span className="badge accent" style={{ fontSize: 9, marginLeft: 6, padding: "1px 6px" }}>constr.</span>}</span><NumField suffix="%" disabled={!admin} value={Math.round(ph.pct * 100)} onChange={(v) => actions.updatePhase(ph.key, v / 100)} /></div>
            ))}
          </div>
          {phaseSum !== 100 && <div style={{ color: "var(--warn)", fontSize: 12, marginTop: 10 }}>Phases should total 100%.</div>}
        </div>
      </div>

      <BuildRates admin={admin} />
    </Page>
  );
}

/* Construction cost per unit area — the studio's own rates, by building type
   and level of finish. These size every project's budget conversation, so they
   sit here and nowhere else: a client never edits a rate.
   Where finished projects have real costed plans, what they actually came to is
   shown beside the rate, so the card can be corrected against reality. */
function BuildRates({ admin }: { admin: boolean }) {
  const rc = useRateCard();
  const { projects } = useStore();
  const rates = rc.buildRates ?? DEFAULT_BUILD_RATES;
  const learned = learnedRates(projects);
  const rateCcy = rc.buildRateCurrency ?? DEFAULT_RATE_CURRENCY;
  const fx = { ...DEFAULT_FX, ...(rc.fx ?? {}) };
  // every currency a project is priced in, plus the ones the table already knows
  const used = Array.from(new Set([...projects.map((p) => String(p.billing?.currency ?? p.currency ?? "USD").toUpperCase()), rateCcy, "GYD"])).filter((c) => c !== "USD").sort();

  return (
    <div className="card pad reveal" style={{ marginTop: 20 }}>
      <div className="row between wrap" style={{ gap: 10, marginBottom: 4 }}>
        <span className="kicker">Construction cost rates</span>
        <span className="dim" style={{ fontSize: 11.5 }}>studio-only · {rateCcy} per ft² unless noted</span>
      </div>
      <div className="panel" style={{ padding: "12px 14px", margin: "10px 0 14px" }}>
        <div className="row wrap" style={{ gap: 14, alignItems: "flex-end" }}>
          <div className="field" style={{ margin: 0, width: 150 }}>
            <label style={{ fontSize: 11 }}>Rates are written in</label>
            <select className="select" disabled={!admin} value={rateCcy} onChange={(e) => actions.setBuildRateCurrency(e.target.value)}>
              {Array.from(new Set(["USD", ...Object.keys(fx)])).sort().map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {used.map((c) => (
            <div key={c} className="field" style={{ margin: 0, width: 130 }}>
              <label style={{ fontSize: 11 }}>{c} per 1 USD</label>
              <input className="input mono" type="number" step="0.01" disabled={!admin} value={fx[c] ?? ""} placeholder="not set"
                onChange={(e) => actions.setFxRate(c, Number(e.target.value) || 0)} style={{ textAlign: "right" }} />
            </div>
          ))}
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.55 }}>
          A project priced in another currency has the rate converted before its budget is sized — a {rateCcy} {rates[0]?.standard ?? 0} rate is {money(Math.round((rates[0]?.standard ?? 0) * ((fx.GYD ?? 1) / (fx[rateCcy] ?? 1))), "GYD")} per ft² on a GYD project.
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12.5, marginBottom: 14, maxWidth: 660 }}>
        What building actually costs, by type and level of finish. These drive the preliminary budget on every project —
        and through it, the design fee. Clients move the budget; only the studio moves the rate.
      </p>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 620 }}>
          <thead><tr>{["Building type", "Standard", "Premium", "Bespoke", "Our own projects"].map((h, i) => (
            <th key={i} style={{ textAlign: i === 0 || i === 4 ? "left" : "right", padding: "9px 10px", borderBottom: "1px solid var(--line)", color: "var(--ink-3)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {rates.map((r) => {
              const l = learned.find((x) => x.type === r.type);
              const gap = l ? Math.round(((l.perUnit - r.standard) / (r.standard || 1)) * 100) : null;
              return (
                <tr key={r.type} style={{ borderBottom: "1px solid var(--line)" }}>
                  <td style={{ padding: "7px 10px" }}>{r.type}</td>
                  {(["standard", "premium", "bespoke"] as const).map((q) => (
                    <td key={q} style={{ padding: "7px 10px", textAlign: "right" }}>
                      <input className="input mono" disabled={!admin} type="number" value={r[q]}
                        onChange={(e) => actions.setBuildRate(r.type, { [q]: Number(e.target.value) || 0 } as any)}
                        style={{ width: 88, textAlign: "right", padding: "5px 8px", fontSize: 12.5 }} />
                    </td>
                  ))}
                  <td style={{ padding: "7px 10px" }}>
                    {l ? (
                      <span className="row" style={{ gap: 7 }}>
                        <span className="mono" style={{ fontSize: 12 }}>{money(l.perUnit)}</span>
                        <span className="badge" style={{ fontSize: 8.5 }}>{l.samples} project{l.samples === 1 ? "" : "s"}</span>
                        {gap !== null && Math.abs(gap) >= 8 && (
                          <span className="badge" style={{ fontSize: 8.5, color: gap > 0 ? "var(--warn)" : "var(--ok)" }}>
                            {gap > 0 ? `+${gap}% vs standard` : `${gap}% vs standard`}
                          </span>
                        )}
                      </span>
                    ) : <span className="dim" style={{ fontSize: 11.5 }}>no costed project yet</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="dim" style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.6 }}>
        “Our own projects” reads the cost plans of finished work — tendered figures where you have them, your rates
        otherwise — divided by the recorded area. It's evidence, not an instruction: correct the rate when you agree with it.
        {learned.length === 0 && " Nothing to compare yet — it fills in as projects get a costed plan and a recorded area."}
      </div>
    </div>
  );
}

function NumField({ value, onChange, prefix, suffix, disabled, step = 0.1 }: { value: number; onChange: (v: number) => void; prefix?: string; suffix?: string; disabled?: boolean; step?: number }) {
  return (
    <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
      {prefix && <span className="dim mono" style={{ fontSize: 13 }}>{prefix}</span>}
      <input className="input mono" type="number" step={step} disabled={disabled} value={value} onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: 84, textAlign: "right", padding: "7px 9px", fontSize: 13, opacity: disabled ? 0.7 : 1 }} />
      {suffix && <span className="dim mono" style={{ fontSize: 13 }}>{suffix}</span>}
    </div>
  );
}
